/**
 * Legal documents — "Regulamin" and "Polityka prywatności".
 *
 * Two static screens that must be readable **before** signing in (the login
 * footer links to them) and **after** (the "O aplikacji" screen does), so they
 * are the only routes with no auth guard that aren't part of the login flow.
 * `renderLegal` therefore picks its chrome from the session: a signed-in reader
 * gets the usual app shell (top bar + drawer), a visitor gets a bare screen with
 * the same back affordance — nothing else, since there is no account to show.
 *
 * The copy below is static, module-local Polish text, like `views/howToLearn.js`
 * — no user data reaches it, which is why it goes into the markup as-is. If a
 * document ever grows a dynamic value, run it through `escapeHtml()` first.
 */

import { isAuthenticated } from '../auth/storage.js';
import { restoreScroll } from '../router.js';
import { backButton, logoMark } from '../components/branding.js';
import { renderAppScreen, wireBack } from '../components/shell.js';
import { LEGAL_META, PRIVACY, TERMS } from './rules.js';

/* -------------------------------------------------------------------------- */
/* Operator data — the one place both documents name us                       */
/* -------------------------------------------------------------------------- */

const PARAGRAPH = 'mt-2 text-sm leading-relaxed text-neutral-600';

function paragraphs(list = []) {
  return list.map((text) => `<p class="${PARAGRAPH}">${text}</p>`).join('');
}

function bullets(list = []) {
  if (!list.length) return '';
  const rows = list
    .map(
      (text) => `
        <li class="flex gap-2.5 text-sm leading-relaxed text-neutral-600">
          <span class="mt-2 h-1 w-1 shrink-0 rounded-full bg-neutral-300"></span>
          <span class="min-w-0">${text}</span>
        </li>`
    )
    .join('');
  return `<ul class="mt-2 space-y-1.5">${rows}</ul>`;
}

/** One numbered section: heading, intro paragraphs, bullets, closing paragraphs. */
function sectionHtml({ title, paragraphs: intro, items, footer }, index) {
  return `
    <section class="mt-7">
      <h2 class="text-sm font-semibold text-neutral-900">${index + 1}. ${title}</h2>
      ${paragraphs(intro)}
      ${bullets(items)}
      ${paragraphs(footer)}
    </section>`;
}

/** The document itself — identical in both chrome variants. */
function documentHtml(doc, otherHref, otherLabel) {
  return `
    <article class="flex flex-1 flex-col pb-10 pt-6">
      <h1 class="text-2xl font-semibold tracking-tight text-neutral-900">${doc.title}</h1>
      <p class="mt-1 text-xs font-medium text-neutral-400">Ostatnia aktualizacja: ${LEGAL_META.lastUpdated}</p>
      <p class="mt-3 text-sm leading-relaxed text-neutral-500">${doc.lead}</p>

      ${doc.sections.map(sectionHtml).join('')}

      <div class="mt-10 border-t border-neutral-200/70 pt-5">
        <a href="${otherHref}"
          class="text-sm font-medium text-neutral-600 underline underline-offset-2 transition hover:text-neutral-900">${otherLabel}</a>
      </div>
    </article>`;
}

/* -------------------------------------------------------------------------- */
/* Screens                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Render one legal document with the chrome that fits the current session.
 *
 * Signed in → the app shell, so the drawer stays reachable. Signed out → a bare
 * screen with the same `backButton()`; it is wired through `wireBack` (the very
 * helper the shell uses), so back means "pop the history entry" here too — these
 * documents are reached from several places (the login footer, "O aplikacji",
 * each other), and only history knows which one it was this time.
 *
 * The `href` fallback only fires on a cold start straight into the document (a
 * deep link, a reload), and it is the one thing that differs between the two:
 * signed in it is the default `#/`, like every other sub-screen, but signed out
 * it has to be `#/login` — `#/` is guarded, so it would forward to `/login`
 * leaving a dead `#/` entry behind, and the system back button would then bounce
 * off it back to the login screen instead of returning to the document.
 */
function renderLegal(mount, doc, { otherHref, otherLabel }) {
  const signedIn = isAuthenticated();
  const contentHtml = documentHtml(doc, otherHref, otherLabel);

  if (signedIn) {
    renderAppScreen(mount, {
      leading: backButton(),
      contentHtml,
      onMount: () => restoreScroll(),
    });
    return;
  }

  // Pre-login: no shell — no menu, no account row, nothing to sign out of.
  // Clear any scroll lock a dialog may have left on <html>, like login/verify do.
  document.documentElement.classList.remove('overflow-hidden');

  mount.className = 'min-h-dvh';
  mount.innerHTML = `
    <div class="mx-auto flex min-h-dvh w-full max-w-sm flex-col px-4">
      <header class="sticky top-0 z-30 -mx-4 flex items-center justify-between bg-white/80 px-4 py-3.5 backdrop-blur">
        ${backButton('#/login')}
        ${logoMark()}
      </header>
      ${contentHtml}
    </div>`;

  wireBack(mount);
  restoreScroll();
}

/** "Regulamin" — route `#/regulamin`, open before and after signing in. */
export function renderTerms(mount) {
  renderLegal(mount, TERMS, {
    otherHref: '#/polityka-prywatnosci',
    otherLabel: 'Polityka prywatności',
  });
}

/** "Polityka prywatności" — route `#/polityka-prywatnosci`, same two states. */
export function renderPrivacy(mount) {
  renderLegal(mount, PRIVACY, { otherHref: '#/regulamin', otherLabel: 'Regulamin' });
}
